import React from 'react';
import {
  CalendarDaysIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  SunIcon,
  InformationCircleIcon,
  ArrowRightIcon,
  DevicePhoneMobileIcon,
  ComputerDesktopIcon,
  UserGroupIcon,
  DocumentTextIcon,
  ShieldCheckIcon,
  CalculatorIcon,
} from '@heroicons/react/24/outline';

export default function HolidayBookHelp() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-cyan-50 to-indigo-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center mb-6">
            <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-cyan-600 rounded-2xl flex items-center justify-center shadow-lg">
              <SunIcon className="h-8 w-8 text-white" />
            </div>
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Holiday Booking System</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Learn how to request time off efficiently with our comprehensive holiday booking system.
            Plan your vacations, manage entitlements, and ensure smooth approval workflows.
          </p>
        </div>

        {/* Overview Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
          <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-8 text-center">
            <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-cyan-600 rounded-xl flex items-center justify-center mx-auto mb-4">
              <CalendarDaysIcon className="h-6 w-6 text-white" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Flexible Booking</h3>
            <p className="text-gray-600">
              Request full days, half days, specific hours, or block bookings to match your schedule needs.
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-8 text-center">
            <div className="w-12 h-12 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-xl flex items-center justify-center mx-auto mb-4">
              <CalculatorIcon className="h-6 w-6 text-white" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Smart Calculations</h3>
            <p className="text-gray-600">
              Automatic entitlement tracking and remaining balance calculations prevent overbooking.
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-8 text-center">
            <div className="w-12 h-12 bg-gradient-to-r from-indigo-500 to-blue-600 rounded-xl flex items-center justify-center mx-auto mb-4">
              <ShieldCheckIcon className="h-6 w-6 text-white" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Approval Workflow</h3>
            <p className="text-gray-600">
              Streamlined approval process with manager notifications and conflict detection.
            </p>
          </div>
        </div>

        {/* Step-by-Step Guide */}
        <div className="bg-white rounded-3xl shadow-2xl border border-gray-200 overflow-hidden mb-12">
          <div className="bg-gradient-to-r from-blue-600 to-cyan-600 px-8 py-6">
            <div className="flex items-center">
              <DocumentTextIcon className="h-8 w-8 text-white mr-4" />
              <h2 className="text-2xl font-bold text-white">How to Book Holiday</h2>
            </div>
          </div>

          <div className="p-8">
            <div className="space-y-8">
              {/* Step 1 */}
              <div className="flex items-start">
                <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-r from-blue-500 to-cyan-600 rounded-xl flex items-center justify-center mr-6">
                  <span className="text-white font-bold text-lg">1</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Choose Holiday Type</h3>
                  <p className="text-gray-600 mb-4">
                    Select the type of holiday you want to book from four options:
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
                      <h4 className="font-semibold text-blue-900 mb-2">Full Day</h4>
                      <p className="text-sm text-blue-700">Complete day off work</p>
                    </div>
                    <div className="bg-cyan-50 rounded-xl p-4 border border-cyan-200">
                      <h4 className="font-semibold text-cyan-900 mb-2">Half Day</h4>
                      <p className="text-sm text-cyan-700">Morning (AM) or afternoon (PM)</p>
                    </div>
                    <div className="bg-indigo-50 rounded-xl p-4 border border-indigo-200">
                      <h4 className="font-semibold text-indigo-900 mb-2">Hours</h4>
                      <p className="text-sm text-indigo-700">Specific time period booking</p>
                    </div>
                    <div className="bg-purple-50 rounded-xl p-4 border border-purple-200">
                      <h4 className="font-semibold text-purple-900 mb-2">Block Booking</h4>
                      <p className="text-sm text-purple-700">Multiple consecutive days</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Step 2 */}
              <div className="flex items-start">
                <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-xl flex items-center justify-center mr-6">
                  <span className="text-white font-bold text-lg">2</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Select Dates</h3>
                  <p className="text-gray-600 mb-4">
                    Choose your holiday dates based on the booking type:
                  </p>
                  <div className="bg-gray-50 rounded-xl p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-2">Single Date Selection</h4>
                        <p className="text-sm text-gray-600 mb-3">For full days and half days</p>
                        <div className="bg-white rounded-lg p-3 border border-gray-200">
                          <p className="text-sm text-gray-700">Click the date picker and select your preferred date</p>
                        </div>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-2">Date Range Selection</h4>
                        <p className="text-sm text-gray-600 mb-3">For block bookings and hours</p>
                        <div className="bg-white rounded-lg p-3 border border-gray-200">
                          <p className="text-sm text-gray-700">Select start and end dates for your holiday period</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Step 3 */}
              <div className="flex items-start">
                <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-r from-indigo-500 to-blue-600 rounded-xl flex items-center justify-center mr-6">
                  <span className="text-white font-bold text-lg">3</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Add Details</h3>
                  <p className="text-gray-600 mb-4">
                    Provide additional information for your holiday request:
                  </p>
                  <div className="space-y-4">
                    <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
                      <h4 className="font-semibold text-blue-900 mb-2">Reason (Optional)</h4>
                      <p className="text-sm text-blue-700">Explain the purpose of your holiday request</p>
                    </div>
                    <div className="bg-orange-50 rounded-xl p-4 border border-orange-200">
                      <h4 className="font-semibold text-orange-900 mb-2">Emergency Leave</h4>
                      <p className="text-sm text-orange-700">Mark as emergency if urgent circumstances require immediate approval</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Step 4 */}
              <div className="flex items-start">
                <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center mr-6">
                  <span className="text-white font-bold text-lg">4</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Review & Submit</h3>
                  <p className="text-gray-600 mb-4">
                    Check your request details and submit for approval:
                  </p>
                  <div className="bg-green-50 rounded-xl p-6 border border-green-200">
                    <div className="flex items-start">
                      <CheckCircleIcon className="h-6 w-6 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                      <div>
                        <h4 className="font-semibold text-green-900 mb-2">Automatic Validation</h4>
                        <ul className="text-sm text-green-700 space-y-1">
                          <li>• Entitlement balance check</li>
                          <li>• Holiday conflict detection</li>
                          <li>• Policy compliance verification</li>
                          <li>• Manager notification</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Features Grid */}
        <div className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-8">Key Features</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
              <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-cyan-600 rounded-lg flex items-center justify-center mb-4">
                <ClockIcon className="h-5 w-5 text-white" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Real-time Calculations</h3>
              <p className="text-gray-600 text-sm">
                See exactly how many hours/days will be deducted and your remaining balance instantly.
              </p>
            </div>

            <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
              <div className="w-10 h-10 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-lg flex items-center justify-center mb-4">
                <ExclamationTriangleIcon className="h-5 w-5 text-white" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Conflict Prevention</h3>
              <p className="text-gray-600 text-sm">
                System automatically detects overlapping holidays and prevents over-entitlement requests.
              </p>
            </div>

            <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
              <div className="w-10 h-10 bg-gradient-to-r from-indigo-500 to-blue-600 rounded-lg flex items-center justify-center mb-4">
                <UserGroupIcon className="h-5 w-5 text-white" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Team Visibility</h3>
              <p className="text-gray-600 text-sm">
                Managers can see team holiday patterns and plan coverage accordingly.
              </p>
            </div>

            <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
              <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center mb-4">
                <InformationCircleIcon className="h-5 w-5 text-white" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Policy Integration</h3>
              <p className="text-gray-600 text-sm">
                Automatically applies your company's holiday policies and working patterns.
              </p>
            </div>

            <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
              <div className="w-10 h-10 bg-gradient-to-r from-cyan-500 to-indigo-600 rounded-lg flex items-center justify-center mb-4">
                <ShieldCheckIcon className="h-5 w-5 text-white" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Approval Tracking</h3>
              <p className="text-gray-600 text-sm">
                Track the status of your holiday requests with clear approval workflow indicators.
              </p>
            </div>

            <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
              <div className="w-10 h-10 bg-gradient-to-r from-indigo-500 to-cyan-600 rounded-lg flex items-center justify-center mb-4">
                <CalculatorIcon className="h-5 w-5 text-white" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Flexible Entitlements</h3>
              <p className="text-gray-600 text-sm">
                Supports both hours-based and days-based holiday entitlements automatically.
              </p>
            </div>
          </div>
        </div>

        {/* Benefits Section */}
        <div className="bg-white rounded-3xl shadow-2xl border border-gray-200 overflow-hidden mb-12">
          <div className="bg-gradient-to-r from-cyan-600 to-blue-600 px-8 py-6">
            <div className="flex items-center">
              <CheckCircleIcon className="h-8 w-8 text-white mr-4" />
              <h2 className="text-2xl font-bold text-white">Benefits & Advantages</h2>
            </div>
          </div>

          <div className="p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">For Employees</h3>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <ArrowRightIcon className="h-5 w-5 text-cyan-500 mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-600">Easy-to-use interface for quick holiday requests</span>
                  </li>
                  <li className="flex items-start">
                    <ArrowRightIcon className="h-5 w-5 text-cyan-500 mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-600">Real-time visibility of remaining holiday entitlement</span>
                  </li>
                  <li className="flex items-start">
                    <ArrowRightIcon className="h-5 w-5 text-cyan-500 mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-600">Flexible booking options for different needs</span>
                  </li>
                  <li className="flex items-start">
                    <ArrowRightIcon className="h-5 w-5 text-cyan-500 mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-600">Instant feedback on request validity</span>
                  </li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">For Managers</h3>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <ArrowRightIcon className="h-5 w-5 text-blue-500 mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-600">Streamlined approval workflow</span>
                  </li>
                  <li className="flex items-start">
                    <ArrowRightIcon className="h-5 w-5 text-blue-500 mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-600">Automatic conflict detection and prevention</span>
                  </li>
                  <li className="flex items-start">
                    <ArrowRightIcon className="h-5 w-5 text-blue-500 mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-600">Team holiday visibility for better planning</span>
                  </li>
                  <li className="flex items-start">
                    <ArrowRightIcon className="h-5 w-5 text-blue-500 mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-600">Policy compliance enforcement</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Troubleshooting */}
        <div className="bg-white rounded-3xl shadow-2xl border border-gray-200 overflow-hidden mb-12">
          <div className="bg-gradient-to-r from-red-600 to-orange-600 px-8 py-6">
            <div className="flex items-center">
              <ExclamationTriangleIcon className="h-8 w-8 text-white mr-4" />
              <h2 className="text-2xl font-bold text-white">Troubleshooting</h2>
            </div>
          </div>

          <div className="p-8">
            <div className="space-y-6">
              <div className="bg-red-50 rounded-xl p-6 border border-red-200">
                <h3 className="text-lg font-bold text-red-900 mb-3">Request Rejected</h3>
                <p className="text-red-700 mb-3">Common reasons why holiday requests might be rejected:</p>
                <ul className="text-sm text-red-700 space-y-1">
                  <li>• Insufficient remaining holiday entitlement</li>
                  <li>• Conflict with existing approved holidays</li>
                  <li>• Policy violations (peak periods, notice requirements)</li>
                  <li>• Emergency leave not properly justified</li>
                </ul>
              </div>

              <div className="bg-yellow-50 rounded-xl p-6 border border-yellow-200">
                <h3 className="text-lg font-bold text-yellow-900 mb-3">Calculation Issues</h3>
                <p className="text-yellow-700 mb-3">If calculations don't match your expectations:</p>
                <ul className="text-sm text-yellow-700 space-y-1">
                  <li>• Check your contracted hours/days per week</li>
                  <li>• Verify holiday policy type (hours vs days)</li>
                  <li>• Exclude bank holidays from calculations</li>
                  <li>• Contact HR for policy clarification</li>
                </ul>
              </div>

              <div className="bg-blue-50 rounded-xl p-6 border border-blue-200">
                <h3 className="text-lg font-bold text-blue-900 mb-3">Technical Issues</h3>
                <p className="text-blue-700 mb-3">If you encounter technical problems:</p>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li>• Refresh the page to reload current data</li>
                  <li>• Check your internet connection</li>
                  <li>• Clear browser cache if dates don't update</li>
                  <li>• Contact IT support for system access issues</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Usage Tips */}
        <div className="bg-white rounded-3xl shadow-2xl border border-gray-200 overflow-hidden">
          <div className="bg-gradient-to-r from-indigo-600 to-blue-600 px-8 py-6">
            <div className="flex items-center">
              <DevicePhoneMobileIcon className="h-8 w-8 text-white mr-4" />
              <ComputerDesktopIcon className="h-8 w-8 text-white mr-4" />
              <h2 className="text-2xl font-bold text-white">Usage Tips</h2>
            </div>
          </div>

          <div className="p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Mobile Best Practices</h3>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <CheckCircleIcon className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-600">Use landscape mode for better date picker visibility</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircleIcon className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-600">Tap and hold dates for quick range selection</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircleIcon className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-600">Scroll horizontally to see all form fields</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircleIcon className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-600">Use voice-to-text for reason field on mobile</span>
                  </li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Desktop Tips</h3>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <CheckCircleIcon className="h-5 w-5 text-blue-500 mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-600">Use keyboard shortcuts (Tab to navigate, Enter to submit)</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircleIcon className="h-5 w-5 text-blue-500 mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-600">Click calendar icon for full calendar view</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircleIcon className="h-5 w-5 text-blue-500 mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-600">Hover over fields for helpful tooltips</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircleIcon className="h-5 w-5 text-blue-500 mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-600">Use browser's date picker for faster input</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}